import { addDays, toDateKey } from '@/domain/reservations/reservation-time';
import type {
  IAssistantActivityItem,
  IAssistantActivityResponse,
  IAssistantPhotoUpdate,
  TAssistantKind,
} from '@/interfaces/assistant.interface';
import type {
  IGhBadge,
  IGhCalloutCard,
  IGhCalloutRow,
  IGhHostAction,
  IGhHostView,
  IGhListCard,
  IGhListRow,
} from '../gpthelp-host-contract';
import {
  bidiIsolateParams,
  clampText,
  fitText,
  joinSentences,
} from './card-text';
import type { ICardFormat } from './card-text';
import { cardId } from './card-ids';
import { changesInventory, ofKind } from './card-kinds';
import { appliedTitle } from './result-titles';
import {
  isOptionGroupRow,
  isProductRow,
  presenterOf,
  rowLabelWithField,
  sizesProductName,
} from './row-presenters';
import {
  addsOtherThanProducts,
  addsProducts,
  summaryAddsOtherThanProducts,
} from './row-values';

/** Where and when the activity is read. */
export interface IActivityContext {
  restaurantId: number;
  storeName: string;
  /** Branch time zone: «hoy» and «ayer» are its days. */
  timeZone: string;
  now: Date;
}

/** One entry of the activity: an assistant action or an update from photos. */
export type TActivityEntry =
  | { kind: 'action'; item: IAssistantActivityItem; at: string }
  | { kind: 'photo'; update: IAssistantPhotoUpdate; at: string };

/** Preview rows an activity card shows before «y {n} más». */
const PREVIEW_ROWS = 3;

/** The «Cambios del asistente» view lists this many actions. */
export const ACTIVITY_VIEW_LIMIT = 20;

/** Actions and updates from photos together, newest first. */
export function mergeActivity(
  response: Pick<IAssistantActivityResponse, 'items' | 'photoUpdates'>
): TActivityEntry[] {
  const entries: TActivityEntry[] = [
    ...response.items.map(
      (item): TActivityEntry => ({ kind: 'action', item, at: item.appliedAt })
    ),
    ...response.photoUpdates.map(
      (update): TActivityEntry => ({
        kind: 'photo',
        update,
        at: update.publishedAt,
      })
    ),
  ];
  return entries.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

/** «hoy 21:40», «ayer 19:02», «jue 25/9 22:10». */
function whenLabel(
  at: string,
  context: IActivityContext,
  fmt: ICardFormat
): string {
  const day = toDateKey(at, context.timeZone);
  const today = toDateKey(context.now, context.timeZone);
  const time = fmt.time(at);
  if (day === today) return fmt.t('assistant.activity.today', { time });
  if (day === addDays(today, -1))
    return fmt.t('assistant.activity.yesterday', { time });
  return `${fmt.date(at)} ${time}`;
}

function badge(
  textKey: string,
  tone: IGhBadge['tone'],
  fmt: ICardFormat,
  icon?: IGhBadge['icon']
): IGhBadge {
  const result: IGhBadge = { text: clampText(fmt.t(textKey), 40), tone };
  if (icon) result.icon = icon;
  return result;
}

function actionBadge(item: IAssistantActivityItem, fmt: ICardFormat): IGhBadge {
  if (item.status === 'applied')
    return badge('assistant.result.applied', 'success', fmt, 'check');
  return (item.undoResult?.kept ?? 0) > 0
    ? badge('assistant.activity.partial', 'warning', fmt)
    : badge('assistant.activity.undone', 'neutral', fmt);
}

function photoBadge(update: IAssistantPhotoUpdate, fmt: ICardFormat): IGhBadge {
  return update.undone
    ? badge('assistant.activity.undone', 'neutral', fmt)
    : badge('assistant.result.applied', 'success', fmt, 'check');
}

/** What an undo rebuilds afterwards: the view's card, or the list card with its length. */
type TUndoRebuild =
  | { rebuild: 'activity' }
  | { rebuild: 'changes'; limit: number };

/**
 * What the undo of an action needs: `created` when it added products,
 * `createdAny` when it added something else (a category, an option group or
 * a choice), as the fallback card says; `optionGroups` when it changed
 * option groups themselves (the pages that list them reload after it);
 * `stock` when it changed the inventory (the pages that show it follow it,
 * plan P5-3).
 */
interface IActionUndoTarget {
  actionId: string;
  created: boolean;
  createdAny?: true;
  optionGroups?: true;
  stock?: true;
}

function undoAction(
  id: 'undo' | 'undoPhoto',
  target: IActionUndoTarget | { sessionId: string },
  rebuild: TUndoRebuild,
  context: IActivityContext,
  fmt: ICardFormat
): IGhHostAction {
  return {
    id,
    kind: 'host',
    label: clampText(fmt.t('assistant.result.undo'), 40),
    icon: 'undo',
    style: 'outline',
    pendingLabel: clampText(fmt.t('assistant.result.undoing'), 40),
    payload: {
      ...target,
      restaurantId: context.restaurantId,
      scopeName: context.storeName,
      ...rebuild,
    },
  };
}

/** Summaries of changes whose rows change option groups themselves (plan P3-1). */
const OPTION_GROUP_CODES: ReadonlyArray<
  IAssistantActivityItem['summary']['code']
> = [
  'createOptionGroup',
  'updateOptionGroup',
  'addOptionChoices',
  'updateOptionChoice',
  'removeOptionChoices',
  'reorderOptionChoices',
];

/**
 * Whether a kind's changes may change option groups themselves. An
 * inventory change never does (plan P5-1: a recipe of a choice is the
 * inventory's).
 */
const MAY_CHANGE_OPTION_GROUPS: Readonly<Record<TAssistantKind, boolean>> = {
  'menu.changes': true,
  'stock.changes': false,
};

/**
 * Whether an action changed option groups themselves: a row of its preview
 * did, or, when the preview leaves applied rows out, its summary says it may
 * have (one of those ops, or a mix of ops); never for a kind that cannot
 * (`MAY_CHANGE_OPTION_GROUPS`).
 */
function changesOptionGroups(item: IAssistantActivityItem): boolean {
  if (!ofKind(MAY_CHANGE_OPTION_GROUPS, item.kind)) return false;
  if (item.preview.some(isOptionGroupRow)) return true;
  if (item.preview.length >= item.appliedCount) return false;
  return (
    item.summary.code === 'mixed' ||
    OPTION_GROUP_CODES.includes(item.summary.code)
  );
}

/**
 * What the undo of an action needs. The preview holds only the first rows,
 * so the summary of a creation counts too.
 */
function actionTarget(item: IAssistantActivityItem): IActionUndoTarget {
  const target: IActionUndoTarget = {
    actionId: item.id,
    created:
      addsProducts(item.preview) ||
      item.summary.code === 'createItem' ||
      item.summary.code === 'duplicateItem',
  };
  if (
    addsOtherThanProducts(item.preview) ||
    summaryAddsOtherThanProducts(item.summary)
  )
    target.createdAny = true;
  if (changesOptionGroups(item)) target.optionGroups = true;
  if (changesInventory(item.kind)) target.stock = true;
  return target;
}

/**
 * The product whose sizes every row of an action changes (plan P4-1), known
 * only when the preview (its first rows) holds them all; null otherwise.
 */
function activitySizesName(item: IAssistantActivityItem): string | null {
  return item.preview.length < item.rowCount
    ? null
    : sizesProductName(item.preview);
}

/**
 * The receipt title of an action («Se cambiaron 11 precios en Bebidas.»,
 * «Se aplicaron 3 cambios de inventario»).
 */
export function activityActionTitle(
  item: IAssistantActivityItem,
  fmt: ICardFormat
): string {
  return appliedTitle(
    item.summary,
    item.appliedCount,
    item.preview[0]?.itemName ?? null,
    fmt,
    activitySizesName(item),
    item.kind
  );
}

/**
 * «Marta · hoy 21:40 · 11 productos» («· 3 cambios» when its preview shows
 * more than products, plan P2-4; the rows past the preview are not known),
 * or who undid it and when.
 */
function actionMeta(
  item: IAssistantActivityItem,
  context: IActivityContext,
  fmt: ICardFormat
): string {
  const user = item.user.name;
  const when = whenLabel(item.appliedAt, context, fmt);
  if (item.status !== 'undone' || !item.undoneAt)
    return fitText(
      fmt,
      item.preview.every(isProductRow)
        ? 'assistant.activity.meta'
        : 'assistant.activity.metaAny',
      { user, when },
      'user',
      160,
      item.appliedCount
    );
  const undoneWhen = whenLabel(item.undoneAt, context, fmt);
  if (item.undoneBy && item.undoneBy.id !== item.user.id)
    return fitText(
      fmt,
      'assistant.activity.metaUndoneBy',
      { user, when, undoer: item.undoneBy.name, undoneWhen },
      'undoer',
      160
    );
  return fitText(
    fmt,
    'assistant.activity.metaUndone',
    { user, when, undoneWhen },
    'user',
    160
  );
}

/** «Marta · hoy 21:40 · 3 precios · 2 nuevos». */
function photoMeta(
  update: IAssistantPhotoUpdate,
  context: IActivityContext,
  fmt: ICardFormat
): string {
  const counts: Array<[string, number]> = [
    ['assistant.activity.count.prices', update.counts.priceChanges],
    ['assistant.activity.count.added', update.counts.added],
    ['assistant.activity.count.hidden', update.counts.hidden],
    ['assistant.activity.count.shown', update.counts.shown],
  ];
  // Joined by hand, so the name and the time are isolated here like the
  // values of a translated sentence (a Latin name next to Arabic counts).
  const user = update.user?.name ?? '';
  const isolated = bidiIsolateParams(fmt.locale, {
    user,
    when: whenLabel(update.publishedAt, context, fmt),
  });
  const parts = [
    user ? String(isolated.user) : '',
    String(isolated.when),
    ...counts
      .filter(([, count]) => count > 0)
      .map(([key, count]) => fmt.tc(key, count)),
  ];
  return clampText(parts.filter((part) => part !== '').join(' · '), 160);
}

/**
 * The first preview rows, before → after, and «y {n} más». A row with no
 * value this release can read (a field of a later phase) says «Cambia»: a
 * row with its label only would read like «y {n} más».
 */
function previewRows(
  item: IAssistantActivityItem,
  fmt: ICardFormat
): IGhCalloutRow[] {
  const shown = item.preview.slice(0, PREVIEW_ROWS);
  const rows = shown.map((row) => {
    const presenter = presenterOf(row);
    const entry: IGhCalloutRow = {
      label: clampText(rowLabelWithField(row, presenter, fmt), 160),
    };
    const before = row.from ? presenter.text(row.from, row, fmt) : null;
    const after = presenter.text(row.to, row, fmt);
    if (before) entry.before = clampText(before, 160);
    if (after) entry.after = clampText(after, 160);
    if (!before && !after)
      entry.after = clampText(fmt.t('assistant.proposal.value.changed'), 160);
    return entry;
  });
  const more = item.appliedCount - shown.length;
  if (more > 0)
    rows.push({
      label: clampText(fmt.t('assistant.activity.more', { n: more }), 160),
    });
  return rows;
}

/** One action of «Cambios del asistente», with «Deshacer» while it can be undone. */
export function activityActionCard(
  item: IAssistantActivityItem,
  context: IActivityContext,
  fmt: ICardFormat
): IGhCalloutCard {
  const card: IGhCalloutCard = {
    id: cardId('activity', item.id),
    v: 1,
    type: 'callout',
    tone: 'neutral',
    badge: actionBadge(item, fmt),
    title: clampText(activityActionTitle(item, fmt), 160),
    text: actionMeta(item, context, fmt),
  };
  const rows = previewRows(item, fmt);
  if (rows.length) card.rows = rows;
  if (item.canUndo)
    card.actions = [
      undoAction(
        'undo',
        actionTarget(item),
        { rebuild: 'activity' },
        context,
        fmt
      ),
    ];
  return card;
}

/** One update from photos, with «Deshacer» while it is the latest and not undone. */
export function activityPhotoCard(
  update: IAssistantPhotoUpdate,
  context: IActivityContext,
  fmt: ICardFormat
): IGhCalloutCard {
  const card: IGhCalloutCard = {
    id: cardId('photo', update.sessionId),
    v: 1,
    type: 'callout',
    tone: 'neutral',
    badge: photoBadge(update, fmt),
    title: clampText(fmt.t('assistant.activity.photo'), 160),
    text: photoMeta(update, context, fmt),
  };
  if (update.canUndo)
    card.actions = [
      undoAction(
        'undoPhoto',
        { sessionId: update.sessionId },
        { rebuild: 'activity' },
        context,
        fmt
      ),
    ];
  return card;
}

/** The card of one activity entry in the view. */
export function activityEntryCard(
  entry: TActivityEntry,
  context: IActivityContext,
  fmt: ICardFormat
): IGhCalloutCard {
  return entry.kind === 'action'
    ? activityActionCard(entry.item, context, fmt)
    : activityPhotoCard(entry.update, context, fmt);
}

/**
 * «Cambios del asistente» (plan §9.9): everything the assistant (and the
 * updates from photos) changed in the store, newest first.
 */
export function activityView(
  response: IAssistantActivityResponse,
  context: IActivityContext,
  fmt: ICardFormat
): IGhHostView {
  const intro = fmt.t('assistant.activity.intro', {
    store: context.storeName,
  });
  return {
    title: fmt.t('assistant.activity.title'),
    // The widget shows the description as one paragraph (no line breaks).
    description: response.nextCursor
      ? joinSentences(fmt.locale, [intro, fmt.t('assistant.activity.limit')])
      : intro,
    cards: mergeActivity(response).map((entry) =>
      activityEntryCard(entry, context, fmt)
    ),
    emptyText: fmt.t('assistant.activity.empty'),
  };
}

function changesListRow(
  entry: TActivityEntry,
  limit: number,
  context: IActivityContext,
  fmt: ICardFormat
): IGhListRow {
  const rebuild: TUndoRebuild = { rebuild: 'changes', limit };
  if (entry.kind === 'action') {
    const { item } = entry;
    const row: IGhListRow = {
      key: `action:${item.id}`,
      title: clampText(activityActionTitle(item, fmt), 160),
      meta: actionMeta(item, context, fmt),
      badge: actionBadge(item, fmt),
    };
    if (item.canUndo)
      row.action = undoAction(
        'undo',
        actionTarget(item),
        rebuild,
        context,
        fmt
      );
    return row;
  }
  const { update } = entry;
  const row: IGhListRow = {
    key: `photo:${update.sessionId}`,
    title: clampText(fmt.t('assistant.activity.photo'), 160),
    meta: photoMeta(update, context, fmt),
    badge: photoBadge(update, fmt),
  };
  if (update.canUndo)
    row.action = undoAction(
      'undoPhoto',
      { sessionId: update.sessionId },
      rebuild,
      context,
      fmt
    );
  return row;
}

/**
 * The `assistant_listChanges` card: the latest changes, each with
 * «Deshacer» while it can be undone (the member taps it; the model never
 * undoes by itself).
 */
export function changesListCard(
  id: string,
  response: Pick<IAssistantActivityResponse, 'items' | 'photoUpdates'>,
  limit: number,
  context: IActivityContext,
  fmt: ICardFormat
): IGhListCard {
  return {
    id,
    v: 1,
    type: 'list',
    title: clampText(fmt.t('assistant.activity.title'), 80),
    rows: mergeActivity(response)
      .slice(0, limit)
      .map((entry) => changesListRow(entry, limit, context, fmt)),
    emptyText: clampText(fmt.t('assistant.activity.empty'), 160),
  };
}
